import { Button, Skeleton, Stack } from '@mui/material';
import { SignatureStatus, useGetDocumentQuery, useZohoEmbedUrlQuery } from 'gql/index';
import { useIntl } from 'react-intl';
import { useEffect, useRef } from 'react';

interface OwnProps {
  requestId: string;
  actionId: string;
  documentId?: number;
  onClose: () => void;
  onSignatureComplete?: (status: 'Signed' | 'Denied') => void;
}

const POLL_INTERVAL_MS = 2000;

export const ZohoSignaturePad: React.FC<OwnProps> = ({ requestId, actionId, documentId, onClose, onSignatureComplete }) => {
  const { formatMessage } = useIntl();
  const hasClosedRef = useRef(false);

  const { data: zohoSignUrl, isFetching } = useZohoEmbedUrlQuery({ requestId, actionId, }, {
    select: d => d.zohoEmbedUrl,
  });

  const { data: document } = useGetDocumentQuery(
    { id: documentId ?? 0 },
    {
      enabled: !!documentId && !!zohoSignUrl,
      refetchInterval: POLL_INTERVAL_MS,
      select: d => d.document
    }
  );

  useEffect(() => {
    if (hasClosedRef.current || !documentId || !document) return;

    const status = document.signatureRequest?.signatureStatus;
    const isComplete = document.isSigned || status === SignatureStatus.Signed || status === SignatureStatus.Denied;
    if (!isComplete) return;

    hasClosedRef.current = true;
    const outcome = status === SignatureStatus.Denied ? 'Denied' : 'Signed';
    onSignatureComplete?.(outcome);
    onClose();
  }, [documentId, document?.isSigned, document?.signatureRequest?.signatureStatus, onClose, onSignatureComplete]);

  return (
    <Stack width='100%' height='100%' position='relative'>
      {isFetching ? <Skeleton width='100%' height='100%' /> : zohoSignUrl ? <>
        <iframe src={zohoSignUrl} style={{ width: '100%', height: '100%', zIndex: 100 }} />

        {documentId && (
          <Stack zIndex={50} position='absolute' top='50%' left='50%' sx={{ transform: 'translate(-50%, -50%)' }}>
            {formatMessage({ id: 'When signing is complete, this window will close automatically.' })}
          </Stack>
        )}
      </> : (
          <Stack gap={2} alignItems='center' justifyContent='center' height='100%'>
           {formatMessage({ id: 'The signature request is not available. It might have expired or have been recalled.' })}
           <Button variant='contained' onClick={() => onClose()}>{formatMessage({ id: 'Close' })}</Button>
         </Stack>
      )}
    </Stack>
  );
};